import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * Pre-Launch Email Confirmation Page (MVP 1.23)
 *
 * Handles email confirmation for pre-launch waitlist signups.
 * Extracts token from URL and confirms via API.
 *
 * Flow:
 * 1. User clicks link in email → /confirm-email-prelaunch?token=xxx
 * 2. This page extracts token and calls API
 * 3. API confirms email and redirects to success page
 *
 * Location: apps/web/src/pages/ConfirmEmailPreLaunchPage.jsx
 * Created: 2025-11-29
 * Part of: MVP 1.23 - Email Confirmation
 */
export default function ConfirmEmailPreLaunchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'token-expired'
  const [errorMessage, setErrorMessage] = useState('');
  const [expiredEmail, setExpiredEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'rate-limited' | 'error'
  const hasConfirmed = useRef(false); // Prevent duplicate API calls (React Strict Mode runs useEffect twice)

  useEffect(() => {
    // Prevent duplicate confirmation attempts
    if (hasConfirmed.current) {
      return;
    }
    hasConfirmed.current = true;

    const confirmEmail = async () => {
      const token = searchParams.get('token');

      // Validation: Token is required
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid confirmation link. Please check your email and try again.');
        return;
      }

      try {
        // Call API to confirm email
        const response = await fetch(`${API_BASE_URL}/api/v1/pre-launch/confirm-email/${token}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (response.ok) {
          // Success - redirect to coming soon page with confirmation
          navigate('/coming-soon?confirmed=true', { replace: true });
        } else {
          // Handle error responses
          const data = await response.json();

          if (data.error === 'TOKEN_EXPIRED') {
            setStatus('token-expired');
            setExpiredEmail(data.email || '');
          } else if (data.error === 'TOKEN_CONSUMED') {
            // Already confirmed - redirect to coming soon page
            navigate('/coming-soon?confirmed=already', { replace: true });
          } else {
            setStatus('error');
            setErrorMessage(data.message || 'An error occurred. Please try again.');
          }
        }
      } catch (error) {
        setStatus('error');
        setErrorMessage('Network error. Please check your connection and try again.');
      }
    };

    confirmEmail();
  }, [searchParams, navigate]);

  const handleResend = async () => {
    if (!expiredEmail || resendStatus === 'sending') {
      return;
    }
    setResendStatus('sending');
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/pre-launch/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: expiredEmail }),
      });
      if (response.status === 429) {
        setResendStatus('rate-limited');
      } else if (response.ok) {
        setResendStatus('sent');
      } else {
        setResendStatus('error');
      }
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-sage-600 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Confirming Your Email...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your email address.
            </p>
          </div>
        )}

        {status === 'token-expired' && (
          <div className="text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            {resendStatus === 'sent' ? (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  New Confirmation Email Sent!
                </h1>
                <p className="text-gray-600 mb-6">
                  Check your inbox for a new confirmation link.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Link Expired
                </h1>
                <p className="text-gray-600 mb-6">
                  This confirmation link has expired. Click below to receive a new one.
                </p>
                {resendStatus === 'rate-limited' && (
                  <p className="text-amber-700 bg-amber-50 px-4 py-2 rounded-lg mb-4 text-sm">
                    Too many attempts. Please try again later.
                  </p>
                )}
                {resendStatus === 'error' && (
                  <p className="text-red-600 bg-red-50 px-4 py-2 rounded-lg mb-4 text-sm">
                    Something went wrong. Please try again.
                  </p>
                )}
                {expiredEmail && resendStatus !== 'rate-limited' && (
                  <button
                    onClick={handleResend}
                    disabled={resendStatus === 'sending'}
                    className="px-6 py-3 bg-sage-600 text-white rounded-lg hover:bg-sage-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                  >
                    {resendStatus === 'sending' ? 'Sending...' : 'Resend Confirmation Email'}
                  </button>
                )}
              </>
            )}
            <div className="mt-4">
              <button
                onClick={() => navigate('/')}
                className="text-sage-600 hover:text-sage-700 font-medium text-sm"
              >
                Return to Home
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Confirmation Failed
            </h1>
            <p className="text-gray-600 mb-6">
              {errorMessage}
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-block px-6 py-3 bg-sage-600 text-white rounded-lg hover:bg-sage-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
